import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  FCM_VAPID_KEY,
  isPushConfigured,
  ONESIGNAL_APP_ID,
  PUSH_PROVIDER,
} from "./pushConfig";

const PushContext = createContext(null);

/**
 * Prepares Web Push for school alerts (notices, submission approvals).
 * Plug in Firebase or OneSignal SDK using env keys — no keys required for offline/PWA core.
 */
export function PushNotificationProvider({ children }) {
  const [permission, setPermission] = useState(
    () => (typeof Notification !== "undefined" ? Notification.permission : "default")
  );
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);

  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  const refreshPermission = useCallback(() => {
    if (typeof Notification !== "undefined") {
      setPermission(Notification.permission);
    }
  }, []);

  const subscribe = useCallback(async () => {
    setError(null);
    if (!supported) {
      setError("Push notifications are not supported in this browser.");
      return null;
    }
    if (!isPushConfigured()) {
      setError(
        "Push is not configured. Add REACT_APP_FCM_VAPID_KEY or REACT_APP_ONESIGNAL_APP_ID to enable."
      );
      return null;
    }

    const perm = await Notification.requestPermission();
    setPermission(perm);
    if (perm !== "granted") {
      setError("Notification permission denied.");
      return null;
    }

    try {
      const reg = await navigator.serviceWorker.ready;

      if (PUSH_PROVIDER === "fcm" && FCM_VAPID_KEY) {
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(FCM_VAPID_KEY),
        });
        setSubscription(sub);
        // TODO: POST sub.toJSON() to your backend with teacher/school id
        console.info("[push] FCM subscription ready — send endpoint to server", sub.endpoint);
        return sub;
      }

      if (PUSH_PROVIDER === "onesignal" && ONESIGNAL_APP_ID) {
        // TODO: OneSignal.init({ appId: ONESIGNAL_APP_ID }) when SDK is added
        console.info("[push] OneSignal app id configured:", ONESIGNAL_APP_ID);
        return null;
      }
    } catch (err) {
      setError(err?.message || "Could not subscribe to push");
    }
    return null;
  }, [supported]);

  useEffect(() => {
    refreshPermission();
  }, [refreshPermission]);

  const value = useMemo(
    () => ({
      supported,
      configured: isPushConfigured(),
      provider: PUSH_PROVIDER,
      permission,
      subscription,
      error,
      subscribe,
      refreshPermission,
    }),
    [supported, permission, subscription, error, subscribe, refreshPermission]
  );

  return <PushContext.Provider value={value}>{children}</PushContext.Provider>;
}

export function usePushNotifications() {
  const ctx = useContext(PushContext);
  if (!ctx) throw new Error("usePushNotifications requires PushNotificationProvider");
  return ctx;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
